import { PAYER_IDENTITY_FIELD } from 'constants/fields';
import { TRACKER_UNKNOWN_VALUE, VISIT_NAME } from 'constants/tracker';
import {
  getPayment,
  getPaymentStatus,
} from 'selectors/entities/payment/payment';
import { getErrors } from 'selectors/errors/errors';
import {
  getCurrentCountry,
  getFieldValue,
  getTotalAmount,
} from 'selectors/ui/ui';
import { OtpChannel } from 'services/payex/otp/types';
import type { Tracker } from 'services/tracker/tracker';
import type { PayexThunkAction } from 'store/configureStore';
import {
  formatNonFXFeedbackResponsesForRequest,
  type NonFXFeedbackResponses,
} from 'utils/formatNonFXFeedbackResponsesForRequest/formatNonFXFeedbackResponsesForRequest';
import type { CouponValidationResponse } from '../../services/apiV3/orders/types';

export const trackEvent = (
  tracker: Tracker,
  name: string,
  ownProps: Record<string, unknown>,
) => {
  const action = 'visit';
  const category = 'visit';
  const label = null;
  const value = null;
  const defaultProps = {
    action,
    category,
    label,
    value,
  };

  const props = { ...defaultProps, ...ownProps };

  return tracker.trackEvent(name, props);
};

export const trackPaymentStatus =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const label = getPaymentStatus(getState());
    const value = window.location.pathname;

    const props = { label, value };
    const name = VISIT_NAME;

    return trackEvent(tracker, name, props);
  };

export const trackPaymentStep =
  (step: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = VISIT_NAME;
    const value = step;

    return trackEvent(tracker, name, { value });
  };

export const trackDocCollectorFileUpload =
  (apiResponse: unknown): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const name = 'Upload';
    const action = 'upload';
    const category = 'document collection';
    const label = getPaymentStatus(getState());
    const value = apiResponse;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackDocCollectorFileDownload =
  (value: string): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const name = 'visit file';
    const category = 'document collection';
    const action = 'click';
    const label = getPaymentStatus(getState());

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackUserFeedback =
  (rating: number, comment: string | undefined): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'submit feedback';
    const category = 'feedback';
    const label = comment;
    const action = 'submit';
    const value = rating;

    const props = { action, category, value, label };

    return trackEvent(tracker, name, props);
  };

export const trackPaymentActions =
  (value: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = `click ${value}`;
    const action = 'click';
    const category = 'payment action';

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackPaymentOption =
  (label: string, value: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = `Payment option ${value}`;
    const action = 'click';
    const category = 'cta payment method';

    const props = { category, label, action, value };

    return trackEvent(tracker, name, props);
  };

export const trackNavigation =
  (internalUrl: string, value: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = `${internalUrl} ${value} navigation`;
    const action = 'click';
    const category = 'navigation';

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackInitiateModal =
  (value: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click';
    const label = 'confirm funds modal';
    const name = `${action} ${value}`;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackConfirmModal =
  (value: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click';
    const label = 'confirm funds modal';
    const name = `${action} ${value}`;

    const props = { action, category, label, value };
    return trackEvent(tracker, name, props);
  };

export const trackCancelPaymentModal =
  (reason: string, comment: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const label = 'cancel modal';
    const action = 'click';
    const name = `${action} ${label}`;

    const props = {
      action,
      category,
      label,
      user_cancellation_reason: reason,
      user_cancellation_comment: comment,
    };

    return trackEvent(tracker, name, props);
  };

export const trackTermsOfUse =
  (link: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'terms of use';
    const action = 'click';
    const value = link;
    const name = `${action} ${link}`;

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackBPGEvent =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click bpg';
    const category = 'cta';
    const action = 'click';
    const value = 'bpg';

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackFieldsErrors =
  (errorReasons: Record<string, string> = {}): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const state = getState();
    const category = 'field';
    const action = 'error';

    return getErrors(state).forEach((field: string) => {
      const name = `field error ${field} ${action}`;

      const props = {
        action,
        category,
        value: errorReasons[field] || 'yes',
        label: field,
      };
      trackEvent(tracker, name, props);
    });
  };

export const trackHelpEvent =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const paymentStatus = getPaymentStatus(getState());
    const label = paymentStatus ? `front-end payment ${paymentStatus}` : null;
    const category = 'cta';
    const action = 'click';
    const value = 'help';
    const name = `${action} ${value}`;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSelectCountry =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const country = getCurrentCountry(getState());
    const label = 'sender_country';
    const category = 'field';
    const action = 'filled';
    const value = country.code;
    const name = label;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackFillAmount =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const amount = getTotalAmount(getState());
    const label = 'amount';
    const category = 'field';
    const action = 'filled';
    const value = `${amount}`;
    const name = label;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackCallToAction =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click dynamic cta';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'dynamic_cta';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackNobookingCtaAction =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click dynamic nobooking cta';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'dynamic_nobooking_cta';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackOppPaymentRequest =
  (cardType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'opp payment';
    const label = 'OPP';
    const action = 'pay';
    const category = 'OPP Payment';
    const value = cardType;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackOppPaymentSuccess =
  (cardType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = 'OPP success';
    const action = 'successful payment';
    const category = 'OPP Payment';
    const value = cardType;
    const name = `${label} ${value}`;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackOppWalletPayment =
  (isStoredCard: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = 'OPP';
    const action = `pay with ${isStoredCard ? 'stored' : 'other'} card`;
    const category = 'Wallet';
    const name = `${label} ${action}`;
    const props = { action, category, label };

    return trackEvent(tracker, name, props);
  };

export const trackOppSaveCardRequest =
  (isCardSavedSuccessfully: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = 'OPP';
    const action = 'store a card';
    const category = 'Wallet';
    const value = isCardSavedSuccessfully ? 'Success' : 'Error';
    const name = `${label} ${action} ${value}`;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackMakeNewPaymentCancel =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'new_payment_cancel';
    const name = 'make new payment after cancel';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackRepeatPayment =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'Repeat payment';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'repeat_payment_my_payments';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackPaymentDetailsMoreInfo =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = 'more info';
    const category = 'visit';
    const action = 'click on more info';
    const value = 'visit_trackingURL_moreinfo';
    const name = `${label} ${action} ${value}`;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackChangePaymentMethod =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click';
    const value = 'change_payment_method';
    const name = 'Change payment method';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackChangePaymentMethodConfirmed =
  (oldPaymentId: string, newPaymentId: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click';
    const value = 'change_payment_method_confirmed';
    const name = 'Change payment method confirmed';
    const props = {
      action,
      category,
      value,
      paymentid: oldPaymentId,
      new_paymentid: newPaymentId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackLrsSubmission =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click';
    const value = 'fill_lrs_online';
    const name = 'LRSonline';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackUploadLrsSignature =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const value = 'save_uploaded_signature_lrs_form';
    const name = 'save_uploaded_signature_lrs_form';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackDrawLrsSignature =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const value = 'save_drawn_signature_lrs_form';
    const name = 'save_drawn_signature_lrs_form';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackWhyIsMyPymtPending =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'pending status';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'check_pending_status';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackOpenPaymentGuideLink =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'payment guide';
    const category = 'cta';
    const action = 'click';
    const value = 'open_help_learn_more';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackClosePaymentGuide =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'exit payment guide';
    const action = 'click on "X" floating card';
    const category = 'cta';
    const value = 'close_payment_guide';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackRefund =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'track refund';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'track_refund';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackRepeatPaymentError =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'repeat payment error';
    const label = null;
    const category = 'error';
    const action = 'error';
    const value = 'repeat_payment_error';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackRepeatPaymentErrorClose =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'close repeat payment error';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'close_repeat_payment_error';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackCheckMarkup =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'check markup';
    const label = null;
    const category = 'cta';
    const action = 'click';
    const value = 'check_markup';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSMSNotification =
  (activated: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'sms notification';
    const label = 'sms_notification';
    const category = 'cta';
    const action = 'click';
    const value = activated ? 'yes' : 'no';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackReturnToClient =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'return_to_client_site';
    const label = null;
    const category = 'cta';
    const action = 'click on return';
    const value = 'return_to_client_site';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSharePaymentDetails =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'share_your_payment';
    const category = 'cta';
    const action = 'click on share';
    const value = 'share_your_payment';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackCopyPaymentLink =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'copy_payment_link';
    const category = 'cta';
    const action = 'click on copy link';
    const value = 'copy_payment_link';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackMissingDestination =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'no_portal_redirection';
    const category = 'navigation';
    const action = 'redirection to select institution';
    const value = document.referrer;
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackRecurringPaymentCancelled =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const label = null;
    const category = 'recurring';
    const action =
      'payer clicks on cancel button from account to cancel recurring';
    const name = 'payer_cancel_recurring_payment';
    const props = { action, category, label };

    return trackEvent(tracker, name, props);
  };

export const trackPayWith =
  (value = 'click pay button'): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = value;
    const category = 'cta';
    const action = 'click';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackPaymentProof =
  (isSuccessful: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const action = 'upload payment proof';
    const label = null;
    const name = 'payment proof uploaded';
    const value = isSuccessful ? 'yes' : 'no';
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackEditDetails =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const name = 'edit_details_review_and_confirm';
    const value = 'edit_details_review_and_confirm';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackSaveEditedDetails =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const name = 'save_edit_details_review_and_confirm';
    const value = 'save_edit_details_review_and_confirm';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackMoreInfo =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const name = 'see_more_info_tracking_page';
    const value = 'see_more_info_tracking_page';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackAuthLetterDownload =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'click option';
    const name = 'auth_letter_download_tracking_page';
    const value = 'auth_letter_download_tracking_page';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackRedirection =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const action = 'redirection';
    const category = 'cta';
    const name = 'redirect_user_gateway_to_client';
    const value = 'redirect_user_gateway_to_client';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackUserLoginSignup =
  (type: string, source: string, origin: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'cta';
    const action = 'display';
    const name = `${type}_${source}_${origin}`;
    const value = name;
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackClickInstalmentPlansTab =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const action = 'click';
    const label = null;
    const value = 'select_instalment_plans_tab';
    const name = value;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackClickSinglePaymentsTab =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const action = 'click';
    const label = null;
    const value = 'select_single_payments_tab';
    const name = value;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSubscriptionWhatsapp =
  (activated: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'whatsapp notification';
    const category = 'cta';
    const action = 'click';
    const value = activated
      ? 'subscription_whatsapp'
      : 'de-subscription_whatsapp';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackPayInAnotherCurrency =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const action = 'click';
    const category = 'payment action';
    const name = 'click_open_nonfxoptions';
    const value = 'click_open_nonfxoptions';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackClickUseINROptions =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_use_INR_option';
    const category = 'payment action';
    const action = 'click';
    const value = 'click_use_INR_option';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackClickUseUSDOptions =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_use_USD_option';
    const category = 'payment action';
    const action = 'click';
    const value = 'click_use_USD_option';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackClickSubmitFXFeedbackForm =
  (nonFXFeedbackResponses: NonFXFeedbackResponses): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_submit_FX_feedbackform';
    const category = 'payment action';
    const action = 'click';
    const value = 'click_submit_FX_feedbackform';
    const props = {
      action,
      category,
      value,
      ...formatNonFXFeedbackResponsesForRequest(nonFXFeedbackResponses),
    };

    return trackEvent(tracker, name, props);
  };

export const trackClickCloseFXFeedbackForm =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_close_FX_feedbackform';
    const category = 'payment action';
    const action = 'click';
    const value = 'click_close_FX_feedbackform';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackErrorReadingFXFormOptions =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'error_reading_fx_form_options';
    const category = 'payment action';
    const action = 'display';
    const value = 'hide_FX_feedbackform';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackEmptyFXFormOptions =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'empty_fx_form_options';
    const category = 'payment action';
    const action = 'display';
    const value = 'hide_FX_feedbackform';
    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackRefundSubmitAccountDetails =
  (paymentId: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_submit_account_details';
    const category = 'refund action';
    const action = 'click';
    const value = 'click_submit_account_details';
    const props = { action, category, value, paymentid: paymentId };

    return trackEvent(tracker, name, props);
  };

export const trackCouponValidation =
  (couponValidation: CouponValidationResponse): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'coupon validation';
    const category = 'payment action';
    const action = 'add_coupon';
    const props = {
      action,
      category,
      value: couponValidation.valid,
      code: couponValidation.code,
      amount: couponValidation.amount,
      currency: couponValidation.currency,
      campaign_id: couponValidation.campaignId,
      rejection_reason: couponValidation.rejectionReason,
    };

    return trackEvent(tracker, name, props);
  };

export const trackSubmitPayerIdentity =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const state = getState();
    const payerIdentity = getFieldValue(state, PAYER_IDENTITY_FIELD);

    const name = 'click_submit_payer_identity';
    const category = 'refund action';
    const action = 'click';
    const value = 'click_submit_payer_identity';

    const props = {
      action,
      category,
      value,
      label: null,
      payer_identity: payerIdentity,
    };

    return trackEvent(tracker, name, props);
  };

export const trackLanguageChange =
  (language: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'change_language';
    const action = 'click option';
    const category = 'cta';
    const value = 'change_language';

    const props = {
      action,
      category,
      label: null,
      language,
      value,
    };

    return trackEvent(tracker, name, props);
  };

export const trackDocVerify2FullScreen =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'doc_verify2.0_full_screen';
    const category = 'cta';
    const action = 'display';
    const value = 'doc_verify2.0_full_screen';

    const props = {
      action,
      category,
      value,
      label: null,
    };

    return trackEvent(tracker, name, props);
  };

const trackOTPEvent =
  (name: string, action: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const category = 'payment action';
    const label = null;
    const value = name;
    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackViewOTPModal = (): PayexThunkAction =>
  trackOTPEvent('view_OTP_modal', 'view');

export const trackVerifyOTPSuccessful =
  (channel: OtpChannel): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_verify_OTP_successful';
    return trackEvent(tracker, name, {
      action: 'click',
      category: 'payment action',
      label: channel,
      value: name,
    });
  };

export const trackVerifyOTPWrong =
  (channel: OtpChannel): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_verify_OTP_wrong';
    return trackEvent(tracker, name, {
      action: 'click',
      category: 'payment action',
      label: channel,
      value: name,
    });
  };

export const trackResendOTPCode =
  (channel: OtpChannel): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_resend_OTP_code';
    return trackEvent(tracker, name, {
      action: 'click',
      category: 'payment action',
      label: channel,
      value: name,
    });
  };

export const trackOTPMethodSelectorShown = (): PayexThunkAction =>
  trackOTPEvent('otp.method_selector_shown', 'view');

export const trackOTPMethodSelected =
  (method: OtpChannel): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'otp.method_selected';
    return trackEvent(tracker, name, {
      action: 'click',
      category: 'payment action',
      label: method,
      value: name,
    });
  };

export const trackOTPMethodSelectorClosed = (): PayexThunkAction =>
  trackOTPEvent('otp.method_selector_closed', 'click');

export const trackCreditCardPaymentRendered =
  (): PayexThunkAction =>
  (_dispatch, getState, { tracker }) => {
    const state = getState();
    const payment = getPayment(state);
    const offer = payment?.offer;
    const country = getCurrentCountry(state);

    const name = 'cc_payment_rendered';
    const action = 'render';
    const category = 'payment flow';

    const getCurrencyCode = () => {
      const price = offer?.price;
      if (typeof price === 'object' && price?.currency) {
        const { currency } = price;
        if (typeof currency === 'object' && 'code' in currency) {
          return (currency as { code: string }).code;
        }
      }
      return TRACKER_UNKNOWN_VALUE;
    };

    const props = {
      action,
      category,
      payment_option: offer?.paymentOption || TRACKER_UNKNOWN_VALUE,
      payment_method_name: offer?.paymentMethodName || TRACKER_UNKNOWN_VALUE,
      payment_method: offer?.paymentMethod || TRACKER_UNKNOWN_VALUE,
      is_surcharge: !!offer?.surcharge,
      currency: getCurrencyCode(),
      country_from: country || TRACKER_UNKNOWN_VALUE,
      recipient_id: payment?.recipient?.id || TRACKER_UNKNOWN_VALUE,
    };

    return trackEvent(tracker, name, props);
  };

export const trackIndiaFxExperimentAssignment =
  (isTestGroup: boolean): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'india_fx_test_assigned';
    const action = 'assigned';
    const category = 'experiment';
    const value = isTestGroup;

    const props = { action, category, value };

    return trackEvent(tracker, name, props);
  };

export const trackDocCollectorExperimentAssignment =
  (
    experimentName: string,
    isProactiveControlsVariant: boolean,
  ): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const action = 'assigned';
    const category = 'experiment';
    const value = isProactiveControlsVariant;

    const props = { action, category, value };

    return trackEvent(tracker, experimentName, props);
  };

export const trackSelectLoanProviderTypeIndia =
  (loanProviderType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_select_loan_provider_type_india';
    const category = 'payment action';
    const action = 'click';
    const label = loanProviderType;
    const value = null;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSelectLoanProviderNameIndia =
  (loanProviderName: string, loanProviderType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_select_loan_provider_name_india';
    const category = 'payment action';
    const action = 'click';
    const label = loanProviderType;
    const value = loanProviderName;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSearchLoanProviderIndia =
  (searchQuery: string, resultsCount: number): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'search_loan_provider_india';
    const category = 'payment action';
    const action = 'search';
    const label = null;
    const value = name;

    const props = {
      action,
      category,
      label,
      value,
      search_query: searchQuery,
      results_count: resultsCount,
    };

    return trackEvent(tracker, name, props);
  };

export const trackDismissLoanProviderModalIndia =
  (
    modalStep: 'provider_type' | 'bank_selection' | 'nbfc_selection',
  ): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'dismiss_loan_provider_modal_india';
    const category = 'payment action';
    const action = 'dismiss';
    const label = null;
    const value = name;

    const props = { action, category, label, value, modal_step: modalStep };

    return trackEvent(tracker, name, props);
  };

export const trackSelectPaymentMethodTypeIndia =
  (paymentMethodType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_select_payment_method_type_india';
    const category = 'payment action';
    const action = 'click';
    const label = paymentMethodType;
    const value = null;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSelectBankNameIndia =
  (bankName: string, paymentMethodType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'click_select_bank_name_india';
    const category = 'payment action';
    const action = 'click';
    const label = paymentMethodType;
    const value = bankName;

    const props = { action, category, label, value };

    return trackEvent(tracker, name, props);
  };

export const trackSearchBankIndia =
  (
    searchQuery: string,
    resultsCount: number,
    paymentMethodType: string,
  ): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'search_bank_india';
    const category = 'payment action';
    const action = 'search';
    const label = null;
    const value = name;

    const props = {
      action,
      category,
      label,
      value,
      search_query: searchQuery,
      results_count: resultsCount,
      payment_method_type: paymentMethodType,
    };

    return trackEvent(tracker, name, props);
  };

export const trackDismissBankModalIndia =
  (paymentMethodType: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'dismiss_bank_modal_india';
    const category = 'payment action';
    const action = 'dismiss';
    const label = null;
    const value = name;

    const props = {
      action,
      category,
      label,
      value,
      modal_step: 'bank_selection',
      payment_method_type: paymentMethodType,
    };

    return trackEvent(tracker, name, props);
  };

export const trackDeadlinesAbTestVisit =
  (recipient: string, userId: string, group: string): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'deadlines_ab.test_visit_payex';
    const props = {
      action: 'visit',
      category: group,
      value: userId,
    };

    return trackEvent(tracker, name, props);
  };

export const trackAutofillTooltipShown =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'show_autofill_tooltip';
    const category = 'payment action';
    const action = 'show';
    const value = 'show_autofill_tooltip';
    const props = { action, category, label: null, value };

    return trackEvent(tracker, name, props);
  };

export const trackAutofillAccountCreated =
  (): PayexThunkAction =>
  (_dispatch, _getState, { tracker }) => {
    const name = 'account_created_autofill_tooltip';
    const category = 'payment action';
    const action = 'account create';
    const value = 'account_created_autofill_tooltip';
    const props = { action, category, label: null, value };

    return trackEvent(tracker, name, props);
  };
